import { apiClient, ApiError } from "@nabble/sdk";
import { Alert, fmt, LoginScreen } from "@nabble/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { peekReturnTo, wasSessionExpired } from "../../../auth/returnTo";
import { destinationAfterStep, type StepResponse } from "../../../auth/session";
import { errorMessage } from "../../../errors";
import { PRIVACY_PATH, TERMS_PATH } from "../../../legal/legal";

export default function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { applyNext, state } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [expired] = useState(() => wasSessionExpired());
  const [returning] = useState(() => peekReturnTo());

  useEffect(() => {
    if (!lockedUntil) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= lockedUntil) setLockedUntil(null);
    }, 1000);
    return () => window.clearInterval(id);
  }, [lockedUntil]);

  async function handleSubmit(values: { email: string; password: string; rememberMe: boolean }): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const { next, rememberedUntil } = await apiClient<StepResponse>("/api/auth/login", {
        method: "POST",
        body: values,
      });
      applyNext(next, rememberedUntil);
      navigate(destinationAfterStep(next));
    } catch (err) {
      if (err instanceof ApiError && err.code === "locked") {
        const seconds = err.body.retryAfterSeconds ?? 0;
        setLockedUntil(seconds > 0 ? Date.now() + seconds * 1000 : null);
        setNow(Date.now());
        setError(seconds > 0 ? null : errorMessage(err, ""));
      } else {
        setLockedUntil(null);
        setError(errorMessage(err, "We couldn't sign you in. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  }

  const locked = lockedUntil !== null && lockedUntil > now;

  return (
    <LoginScreen
      onSubmit={(v) => void handleSubmit(v)}
      loading={loading}
      rememberMeDays={state?.rememberMeDays ?? 0}
      error={locked ? `For security, we've blocked access for a few minutes. You can try again in ${fmt.countdown(lockedUntil - now)}.` : (error ?? undefined)}
      errorTone={locked ? "warning" : "danger"}
      errorTitle={locked ? "Account temporarily locked" : undefined}
      notice={
        expired ? (
          <Alert tone="info" icon="clock" title="Your session expired. Please sign in again.">
            {returning ? "Once you sign in, you'll go back to the page you were on." : undefined}
          </Alert>
        ) : null
      }
      footer={
        <p className="nb-small nb-subtle" style={{ textAlign: "center", margin: 0 }}>
          <Link to={PRIVACY_PATH}>Privacy notice</Link>
          {" · "}
          <Link to={TERMS_PATH}>Terms of use</Link>
        </p>
      }
      forgotLink={
        <Link to="/forgot-password" className="nb-small">
          Forgot your password?
        </Link>
      }
    />
  );
}
